/**
 * A Mate engine conversation's subscription (`subscribeEngineConversation`) into the account: its
 * window snapshot, then each commit's changes, delivered atomically across the header, runs, items
 * and requests; streamed text into the live registry, never the store. A cursor per conversation
 * (`{epoch, origin, seq}`) resumes it after a drop or a release; a gap resubscribes from it; a
 * reset, another sequence space or an epoch from the past forget what was held first. The last
 * release keeps the conversation for {@link ENGINE_FORGET_AFTER_MS}; then it is forgotten. An
 * authoritative denial forgets it at once.
 *
 * @module data/adapters/mateEngine
 */
import {
  ENGINE_WIRE_BUDGETS,
  EnvironmentAuthorizationError,
  MATE_ENGINE_PROTOCOLS,
  WS_METHODS,
  type ConversationHeader,
  type EngineConversationFrame,
  type EnvironmentId,
  type EngineCursor,
  type EngineDetail,
  type EnginePage,
  type EngineRowsFrame,
  type Item,
  type Request,
  type RunRecord,
} from "@t3tools/contracts";
import * as Cause from "effect/Cause";
import * as Clock from "effect/Clock";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Fiber from "effect/Fiber";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { Atom } from "effect/reactivity";

import type { EnvironmentRegistry } from "../../connection/registry.ts";
import { EnvironmentSupervisor } from "../../connection/supervisor.ts";
import { request } from "../../rpc/client.ts";
import type { RpcSession } from "../../rpc/session.ts";

import type { EngineLiveText } from "../engineLive.ts";
import {
  engineConversationId,
  engineConversationLink,
  engineConversationScopes,
  engineEarlierScope,
  engineFactId,
  engineRowsLink,
  engineRowsScope,
  type EngineConversationKey,
  type EngineSpanValue,
} from "../families/mateEngine.ts";
import type { FamilyValues, LinkKey, Revision, ScopeKey } from "../model.ts";
import { streamOf, type Row } from "../reducer.ts";
import { earlierOf } from "../projections/mateEngine.ts";
import { readsOfState, type AccountStore } from "../store.ts";
import type { StreamEvent, StreamFault } from "../streamMachine.ts";
import { superviseLink } from "../supervisor.ts";

/** How long a released conversation is kept, so a return resumes instead of reading it again. */
export const ENGINE_FORGET_AFTER_MS = 5 * 60 * 1_000;

/**
 * How many pages of what a finished run's result draws from it reads before its card is drawn. A
 * page is up to the wire's `runPageItems`.
 */
export const ENGINE_RUN_PAGES = 5;

/** Where a page of a run is read from: before a sequence, after one, or its newest. */
export interface RunPageAt {
  readonly before?: number;
  readonly after?: number;
  readonly only?: "outcome";
}

/** How many runs read their missing items at once. */
const RUN_READS_AT_ONCE = 4;

/** The engine's wire as this adapter reads it; the transport and its session are the wire's. */
export interface EngineConversationWire {
  readonly subscribe: (
    key: EngineConversationKey,
    after: EngineCursor | null,
  ) => Stream.Stream<EngineConversationFrame, StreamFault>;
  readonly subscribeRows: (environmentId: string) => Stream.Stream<EngineRowsFrame, StreamFault>;
  /** Older run groups, before the run of `beforeOrdinal`: their runs, words, answers, requests. */
  readonly readEarlier: (
    key: EngineConversationKey,
    beforeOrdinal: number,
  ) => Effect.Effect<EnginePage, StreamFault>;
  /**
   * A run's items: its newest page, the page `before` a sequence, the oldest page `after` one;
   * `only: "outcome"` reads just what its closed card draws its result from.
   */
  readonly readRun: (
    key: EngineConversationKey,
    runId: string,
    at: RunPageAt,
  ) => Effect.Effect<EnginePage, StreamFault>;
  /** An item's whole part (`engine.readDetail`): a cut message, a call's output or result. */
  readonly readDetail: (
    key: EngineConversationKey,
    itemId: string,
    part: string,
  ) => Effect.Effect<EngineDetail, StreamFault>;
  /** A height this client laid a published page out at (`engine.recordPageHeight`). */
  readonly recordPageHeight?: (
    key: EngineConversationKey,
    itemId: string,
    width: number,
    height: number,
  ) => Effect.Effect<void, StreamFault>;
  /**
   * The Mate's access as its connection learns it: a fault, or `null` each time it connects (a new
   * session, or the network back under the old one), verified again.
   */
  readonly watch?: (
    environmentId: string,
    receive: (fault: StreamFault | null) => void,
  ) => Effect.Effect<void>;
}

const isAuthorization = Schema.is(EnvironmentAuthorizationError);

/** A failure of the wire's call, classified: the account's rules decide what follows. */
export function classifyEngineFailure(cause: Cause.Cause<unknown>): StreamFault {
  const error = Cause.findErrorOption(cause);
  if (Option.isSome(error)) {
    const value = error.value;
    if (typeof value === "object" && value !== null && "outcome" in value)
      return value as StreamFault;
    if (isAuthorization(value)) return { outcome: "authoritative-denial", message: value.message };
  }
  const squashed = Cause.squash(cause);
  return {
    outcome: "transient",
    message: squashed instanceof Error ? squashed.message : String(squashed),
  };
}

/** The refusal an `unserved` frame is: the update route, or a Mate whose conversation is on V1. */
export const unservedFault = (unserved: {
  readonly reason: string;
  readonly message: string;
}): StreamFault => ({
  outcome: "definitive-refusal",
  code: unserved.reason === "protocol" ? "update" : "not-on-engine",
  message: unserved.message,
});

/** The highest protocol this build and the Mate both speak; `null` when they share none. */
export function engineProtocol(served: ReadonlyArray<number> | number | undefined): number | null {
  const theirs = typeof served === "number" ? [served] : (served ?? []);
  const shared = MATE_ENGINE_PROTOCOLS.filter((protocol) => theirs.includes(protocol));
  return shared.length === 0 ? null : Math.max(...shared);
}

const PROTOCOL = Math.max(...MATE_ENGINE_PROTOCOLS);

/** The engine's wire over the Mate's own socket: each call on its current session. */
export function makeEngineConversationWire(
  registry: EnvironmentRegistry["Service"],
): EngineConversationWire {
  const onSession = <A, E>(
    environmentId: string,
    open: (client: RpcSession["client"]) => Stream.Stream<A, E>,
  ): Stream.Stream<A, StreamFault> =>
    registry
      .followStream(
        environmentId as EnvironmentId,
        Stream.unwrap(
          Effect.map(EnvironmentSupervisor, (supervisor) =>
            SubscriptionRef.changes(supervisor.session).pipe(
              Stream.filter(Option.isSome),
              Stream.take(1),
              Stream.flatMap((session) => open(session.value.client)),
            ),
          ),
        ),
      )
      .pipe(Stream.catchCause((cause) => Stream.fail(classifyEngineFailure(cause))));
  return {
    subscribe: (key, after) =>
      onSession(key.environmentId, (client) =>
        client[WS_METHODS.subscribeEngineConversation]({
          protocol: PROTOCOL,
          conversationId: key.conversationId as never,
          ...(after === null ? {} : { after }),
        }),
      ),
    subscribeRows: (environmentId) =>
      onSession(environmentId, (client) =>
        client[WS_METHODS.subscribeEngineRows]({ protocol: PROTOCOL }),
      ),
    readEarlier: (key, beforeOrdinal) =>
      registry
        .run(
          key.environmentId as EnvironmentId,
          request(WS_METHODS.engineReadEarlier, {
            protocol: PROTOCOL,
            conversationId: key.conversationId as never,
            beforeOrdinal,
          }),
        )
        .pipe(Effect.catchCause((cause) => Effect.fail(classifyEngineFailure(cause)))),
    readRun: (key, runId, at) =>
      registry
        .run(
          key.environmentId as EnvironmentId,
          request(WS_METHODS.engineReadRun, {
            protocol: PROTOCOL,
            conversationId: key.conversationId as never,
            runId: runId as never,
            ...(at.before === undefined ? {} : { beforeSeq: at.before }),
            ...(at.after === undefined ? {} : { afterSeq: at.after }),
            ...(at.only === undefined ? {} : { only: at.only }),
          }),
        )
        .pipe(Effect.catchCause((cause) => Effect.fail(classifyEngineFailure(cause)))),
    readDetail: (key, itemId, part) =>
      registry
        .run(
          key.environmentId as EnvironmentId,
          request(WS_METHODS.engineReadDetail, {
            protocol: PROTOCOL,
            conversationId: key.conversationId as never,
            itemId: itemId as never,
            part,
          }),
        )
        .pipe(Effect.catchCause((cause) => Effect.fail(classifyEngineFailure(cause)))),
    recordPageHeight: (key, itemId, width, height) =>
      registry
        .run(
          key.environmentId as EnvironmentId,
          request(WS_METHODS.engineRecordPageHeight, {
            protocol: PROTOCOL,
            conversationId: key.conversationId as never,
            itemId: itemId as never,
            width,
            height,
          }),
        )
        .pipe(
          Effect.asVoid,
          Effect.catchCause((cause) => Effect.fail(classifyEngineFailure(cause))),
        ),
    watch: (environmentId, receive) =>
      registry.stateChanges(environmentId as EnvironmentId).pipe(
        Stream.runForEach((state) =>
          Effect.sync(() => {
            if (
              state.phase === "blocked" &&
              state.lastFailure?._tag === "ConnectionBlockedError" &&
              ["authentication", "permission", "read-only", "not-found"].includes(
                state.lastFailure.reason,
              )
            )
              receive({
                outcome:
                  state.lastFailure.reason === "authentication"
                    ? "access-unverified"
                    : "authoritative-denial",
                message: state.lastFailure.message,
              });
            else if (state.phase === "connected") receive(null);
          }),
        ),
        Effect.catch(() =>
          Effect.sync(() =>
            receive({
              outcome: "authoritative-denial",
              message: "This Mate is no longer registered.",
            }),
          ),
        ),
      ),
  };
}

const RESUBSCRIBE = Symbol("resubscribe");
type Resubscribe = typeof RESUBSCRIBE;

type Records = Extract<EngineConversationFrame, { type: "changes" }>;

type Window = Extract<EngineConversationFrame, { type: "snapshot" }>["window"];

/** A frame's records as rows: each stamped with the epoch it was served in and its own `rev`. */
function rowsOf(
  key: EngineConversationKey,
  epoch: number,
  frame: Pick<Records, "runs" | "items" | "requests"> & {
    readonly header?: Records["header"];
    readonly head: number;
  },
  window: Window | undefined,
): ReadonlyArray<Row> {
  const { environmentId } = key;
  const revision = (seq: number): Revision => ({
    kind: "mate-conversation",
    environmentId,
    epoch,
    seq,
  });
  const rows: Row[] = [];
  if (frame.header !== undefined && window !== undefined)
    rows.push({
      family: "mateEngineConversation",
      id: engineConversationId(key),
      value: { environmentId, header: frame.header, window },
      revision: revision(frame.head),
    });
  for (const run of frame.runs)
    rows.push({
      family: "mateEngineRun",
      id: engineFactId(environmentId, run.id),
      value: { ...run, environmentId },
      revision: revision(run.rev),
    });
  for (const item of frame.items)
    rows.push({
      family: "mateEngineItem",
      id: engineFactId(environmentId, item.id),
      value: { ...item, environmentId },
      revision: revision(item.rev),
    });
  for (const request of frame.requests)
    rows.push({
      family: "mateEngineRequest",
      id: engineFactId(environmentId, request.id),
      value: { ...request, environmentId },
      revision: revision(request.rev),
    });
  return rows;
}

interface Held {
  count: number;
  stop: () => void;
  retry: () => void;
  recover: () => void;
  /** Its transport is back: a retry it waits out is tried now; a refusal stays. */
  resume: () => void;
  fault: (fault: StreamFault) => void;
}

/**
 * One held link: its supervisor runs while any holder holds it; the last release starts `grace`,
 * after which `expire` runs; a hold inside it cancels it.
 */
function makeLinks(options: {
  readonly store: AccountStore;
  readonly setTimer: (callback: () => void, delayMs: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
}) {
  const held = new Map<LinkKey, Held>();
  const expiring = new Map<LinkKey, unknown>();
  let closed = false;
  const hold = (
    link: LinkKey,
    scopes: ReadonlyArray<ScopeKey>,
    attempt: () => Effect.Effect<never, StreamFault>,
    release: { readonly graceMs: number; readonly expire: () => void } | null,
  ) => {
    if (closed) return () => {};
    const timer = expiring.get(link);
    if (timer !== undefined) {
      options.clearTimer(timer);
      expiring.delete(link);
    }
    let entry = held.get(link);
    if (entry === undefined) {
      // A fault told to the attempt under way ends it; told with none under way, it is the next
      // attempt's own check (the Mate's access) that decides, never a stale fault.
      let current: Deferred.Deferred<never, StreamFault> | null = null;
      const supervisor = Effect.runSync(
        superviseLink({
          key: link,
          scopes,
          store: options.store,
          attempt: () =>
            Effect.gen(function* () {
              const told = yield* Deferred.make<never, StreamFault>();
              current = told;
              return yield* Effect.raceFirst(attempt(), Deferred.await(told));
            }).pipe(
              Effect.ensuring(
                Effect.sync(() => {
                  current = null;
                }),
              ),
            ),
          repairSession: Effect.fail({
            outcome: "definitive-refusal",
            message: "Reconnect this Mate to verify access.",
          }),
        }),
      );
      const fiber = Effect.runFork(supervisor.run);
      entry = {
        count: 0,
        stop: () => {
          Effect.runSync(supervisor.release);
          void Effect.runFork(Fiber.interrupt(fiber));
        },
        retry: () => void Effect.runFork(supervisor.signal("manual-retry")),
        recover: () => void Effect.runFork(supervisor.signal("input-changed")),
        resume: () => void Effect.runFork(supervisor.signal("resume")),
        fault: (fault) => {
          if (current !== null) Deferred.doneUnsafe(current, Exit.fail(fault));
        },
      };
      held.set(link, entry);
    }
    const active = entry;
    active.count++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--active.count > 0) return;
      active.stop();
      held.delete(link);
      if (release === null || closed) return;
      expiring.set(
        link,
        options.setTimer(() => {
          expiring.delete(link);
          if (!closed && !held.has(link)) release.expire();
        }, release.graceMs),
      );
    };
  };
  return {
    hold,
    held: (link: LinkKey) => held.get(link),
    close: () => {
      closed = true;
      for (const entry of held.values()) entry.stop();
      held.clear();
      for (const timer of expiring.values()) options.clearTimer(timer);
      expiring.clear();
    },
  };
}

export function makeMateEngineConversations(options: {
  readonly store: AccountStore;
  readonly wire: EngineConversationWire;
  readonly live: EngineLiveText;
  readonly forgetAfterMs?: number;
  readonly setTimer: (callback: () => void, delayMs: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
}) {
  const { store, wire, live } = options;
  const links = makeLinks(options);
  const cursors = new Map<string, EngineCursor>();
  /** Every conversation held or kept, by environment: whom a denial forgets. */
  const known = new Map<string, Map<string, EngineConversationKey>>();
  const watches = new Map<string, Fiber.Fiber<void>>();
  const withheld = new Map<string, StreamFault>();
  let closed = false;

  const now = () => Effect.runSync(Clock.currentTimeMillis);
  const signal = (key: LinkKey | ScopeKey, event: StreamEvent) =>
    store.dispatch({ kind: "stream", key, now: now(), event });
  const reads = new Set<Fiber.Fiber<unknown>>();
  /** The order this tab heard live gauges in: a later one replaces an earlier one. */
  let heard = 0;

  /** A run's span as a row: the order this tab heard it in orders it. */
  const spanRow = (key: EngineConversationKey, span: Omit<EngineSpanValue, "environmentId">) =>
    ({
      family: "mateEngineSpan",
      id: engineFactId(key.environmentId, span.runId),
      value: { ...span, environmentId: key.environmentId },
      revision: { kind: "mate-link", sequence: ++heard },
    }) satisfies Row;

  /**
   * What the runs a frame or a page carries lack of their own items, before they are drawn. A
   * card's closed face needs none of its run's items but what its result draws from: its worked
   * line counts its effort from the run's summary. So a finished run that made calls reads just
   * those (`only: "outcome"`, one page however long the run, up to `ENGINE_RUN_PAGES`), and a live
   * one reads nothing — the window carries its newest items, under the lines its card opens on.
   * Each such run says what stretch of its lines it holds (`mateEngineSpan`): a finished one none
   * until its card opens, a live one from its earliest held; the rest are read a page at a time
   * as its card opens and scrolls (`readRunPage`). A read that fails leaves the run with what it
   * holds, its span all the same. A finished run already read from its start keeps its span (its
   * pages stay held across a resubscribe), and reads nothing again.
   */
  const wholeRuns = (
    key: EngineConversationKey,
    runs: ReadonlyArray<RunRecord>,
    items: ReadonlyArray<Item>,
  ): Effect.Effect<{
    readonly items: ReadonlyArray<Item>;
    readonly requests: ReadonlyArray<Request>;
    readonly spans: ReadonlyArray<Omit<EngineSpanValue, "environmentId">>;
  }> =>
    Effect.gen(function* () {
      const read = readsOfState(store.state());
      const lacking = runs.flatMap((run) => {
        const held = new Map<string, Item>(
          items.flatMap((item) => (item.runId === run.id ? [[item.id as string, item]] : [])),
        );
        for (const id of read.index("engineItemsOfRun", engineFactId(key.environmentId, run.id))) {
          const fact = read.fact("mateEngineItem", id);
          if (fact.kind === "known" && !held.has(fact.value.id))
            held.set(fact.value.id, fact.value);
        }
        return held.size < run.summary.items ? [{ run, held }] : [];
      });
      if (lacking.length === 0) return { items: [], requests: [], spans: [] };
      const found = yield* Effect.forEach(
        lacking,
        ({ run, held }) =>
          Effect.gen(function* () {
            const span = (from: number | null, to: number | null) => ({
              conversationId: key.conversationId,
              runId: run.id,
              from,
              to,
              reading: null,
            });
            if (run.end === null) {
              // Held from its live tail: the newest items a window carries. A window also pins a
              // run's person messages, requests and published pages however old: counting those
              // claimed everything between them and the tail was held, and it was never read.
              const lines = [...held.values()]
                .sort((a, b) => b.seq - a.seq)
                .slice(0, ENGINE_WIRE_BUDGETS.liveTailItems)
                .filter((item) => item.kind !== "person");
              const from =
                lines.length === 0
                  ? (run.summary.lastItemSeq ?? 0) + 1
                  : Math.min(...lines.map((item) => item.seq));
              return { items: [], requests: [], spans: [span(from, null)] };
            }
            // Read from its start before (a resubscribe to the same sequence space; a reset or
            // another space forgot it): what it read stays held, so its card keeps its pages.
            const kept = read.fact("mateEngineSpan", engineFactId(key.environmentId, run.id));
            if (kept.kind === "known" && kept.value.from === null)
              return { items: [], requests: [], spans: [span(null, kept.value.to)] };
            const items: Item[] = [];
            const requests: Request[] = [];
            if (Object.keys(run.summary.calls).length > 0)
              yield* Effect.gen(function* () {
                let after = 0;
                for (let page = 0; page < ENGINE_RUN_PAGES; page++) {
                  const answer = yield* wire.readRun(key, run.id, { after, only: "outcome" });
                  if (answer._tag !== "Page") return;
                  for (const item of answer.items)
                    if (!held.has(item.id)) {
                      held.set(item.id, item);
                      items.push(item);
                    }
                  requests.push(...answer.requests);
                  if (!answer.more || answer.items.length === 0) return;
                  after = Math.max(...answer.items.map((item) => item.seq));
                }
              }).pipe(Effect.orElseSucceed(() => undefined));
            return { items, requests, spans: [span(null, 0)] };
          }),
        { concurrency: RUN_READS_AT_ONCE },
      );
      return {
        items: found.flatMap((run) => run.items),
        requests: found.flatMap((run) => run.requests),
        spans: found.flatMap((run) => run.spans),
      };
    });

  /** A thought held only by its preview: the rest is read on demand, as the import kept it. */
  const cutThought = (item: Item) =>
    item.kind === "thought" && !item.streaming && item.preview.length < item.length;
  /** A message the wire cut to its budget: its words are read whole before it is drawn. */
  const cutMessage = (item: Item) =>
    (item.kind === "note" || item.kind === "person") && item.cut?.part === "text";

  /**
   * Items as a card draws them: a call's result the wire cut (a document its card decodes whole)
   * read back whole first; one whose whole cannot be read says it was too long, as V1's does. A
   * thought longer than its preview is read whole too, as V1 holds its reasoning; one that cannot
   * be keeps its preview. A message the wire cut is read whole too; one whose whole cannot be
   * read keeps what came.
   */
  const wholeResults = (
    key: EngineConversationKey,
    items: ReadonlyArray<Item>,
  ): Effect.Effect<ReadonlyArray<Item>> =>
    items.some(
      (item) =>
        (item.kind === "call" && item.cut?.part === "result") ||
        cutThought(item) ||
        cutMessage(item),
    )
      ? Effect.forEach(
          items,
          (item): Effect.Effect<Item> => {
            if (cutMessage(item) && (item.kind === "note" || item.kind === "person"))
              return wire.readDetail(key, item.id, "text").pipe(
                Effect.map((detail): Item => {
                  if (detail._tag !== "Detail" || detail.from !== 0 || detail.to !== detail.total)
                    return item;
                  const { cut: _cut, ...uncut } = item;
                  return { ...uncut, text: detail.text };
                }),
                Effect.orElseSucceed(() => item),
              );
            if (cutThought(item) && item.kind === "thought")
              return wire.readDetail(key, item.id, "detail").pipe(
                Effect.map((detail): Item =>
                  detail._tag === "Detail" && detail.from === 0 && detail.to === detail.total
                    ? { ...item, preview: detail.text }
                    : item,
                ),
                Effect.orElseSucceed(() => item),
              );
            if (item.kind !== "call" || item.cut?.part !== "result" || item.result === undefined)
              return Effect.succeed(item);
            const { cut: _cut, ...uncut } = item;
            const result = item.result;
            const tooLong: Item = { ...uncut, result: { ...result, truncated: true } };
            return wire.readDetail(key, item.id, "result").pipe(
              Effect.map((detail): Item =>
                detail._tag === "Detail" && detail.from === 0 && detail.to === detail.total
                  ? { ...uncut, result: { ...result, resultText: detail.text } }
                  : tooLong,
              ),
              Effect.orElseSucceed(() => tooLong),
            );
          },
          { concurrency: RUN_READS_AT_ONCE },
        )
      : Effect.succeed(items);

  const forget = (key: EngineConversationKey) => {
    cursors.delete(engineConversationId(key));
    live.forget(key);
    store.dispatch({ kind: "forget", scopes: Object.values(engineConversationScopes(key)) });
  };

  const receiveAccess = (environmentId: string, fault: StreamFault | null) => {
    if (closed) return;
    const conversations = [...(known.get(environmentId)?.values() ?? [])];
    if (fault === null) {
      if (withheld.delete(environmentId)) {
        links.held(engineRowsLink(environmentId))?.retry();
        for (const key of conversations) links.held(engineConversationLink(key))?.retry();
        return;
      }
      // Connected again, as the transport skips its own wait on its return: every link of the
      // Mate's that waits out a retry tries now. Kara's rows slept out the rest of a 60 s rung
      // after a lost socket while its freshly held conversation opened at once, and the card at the
      // turn's foot listed a helper its records had already closed.
      links.held(engineRowsLink(environmentId))?.resume();
      for (const key of conversations) links.held(engineConversationLink(key))?.resume();
      return;
    }
    withheld.set(environmentId, fault);
    for (const key of conversations) {
      if (fault.outcome === "authoritative-denial") forget(key);
      links.held(engineConversationLink(key))?.fault(fault);
    }
  };

  const attempt = (key: EngineConversationKey) =>
    Effect.gen(function* () {
      const access = withheld.get(key.environmentId);
      if (access !== undefined) return yield* Effect.fail(access);
      const link = engineConversationLink(key);
      const scopes = Object.values(engineConversationScopes(key));
      const id = engineConversationId(key);
      for (;;) {
        signal(link, { kind: "handshake" });
        for (const scope of scopes) {
          signal(scope, { kind: "attempt" });
          signal(scope, { kind: "handshake" });
        }
        const generations = scopes.map((scope) => ({
          scope,
          generation: streamOf(store.state(), scope).generation,
        }));
        /** The header of a split commit's first part, until the part that moves the cursor. */
        let splitHeader: { readonly from: number; readonly header: ConversationHeader } | undefined;
        /** An item whose record arrives whole drops its streamed text, settle frame or not. */
        const settleClosed = (items: ReadonlyArray<Item>) => {
          for (const item of items)
            if (
              ((item.kind === "note" || item.kind === "thought") && !item.streaming) ||
              (item.kind === "call" && item.state !== "running")
            )
              live.settle(key, item.id);
        };
        const deliver = (rows: ReadonlyArray<Row>, reset: boolean) =>
          store.dispatch({
            kind: "delivery",
            via: "mate-direct",
            scopes: generations,
            reset,
            ...(reset ? { partial: true } : {}),
            rows,
            removals: [],
          });
        const handle = (
          frame: EngineConversationFrame,
        ): Effect.Effect<void, StreamFault | Resubscribe> =>
          Effect.suspend((): Effect.Effect<void, StreamFault | Resubscribe> => {
            if (closed) return Effect.void;
            // Denied or unverified: nothing more of it is taken, even a frame already on its way.
            const access = withheld.get(key.environmentId);
            if (access !== undefined) return Effect.fail(access);
            const cursor = cursors.get(id);
            switch (frame.type) {
              case "snapshot": {
                if (
                  cursor !== undefined &&
                  (cursor.origin !== frame.origin || frame.epoch < cursor.epoch)
                )
                  forget(key);
                return Effect.gen(function* () {
                  const lacked = yield* wholeRuns(key, frame.runs, frame.items);
                  const items = yield* wholeResults(key, [...frame.items, ...lacked.items]);
                  // A denial told while the runs were read keeps nothing of this frame either.
                  if (closed || withheld.has(key.environmentId)) return;
                  const whole = {
                    ...frame,
                    items,
                    requests: [...frame.requests, ...lacked.requests],
                  };
                  Atom.batch(() =>
                    deliver(
                      [
                        ...rowsOf(key, frame.epoch, whole, frame.window),
                        ...lacked.spans.map((span) => spanRow(key, span)),
                      ],
                      true,
                    ),
                  );
                  settleClosed(whole.items);
                  cursors.set(id, { epoch: frame.epoch, origin: frame.origin, seq: frame.head });
                });
              }
              case "changes": {
                if (cursor === undefined || frame.from !== cursor.seq)
                  return Effect.fail(RESUBSCRIBE);
                const held = store.state().facts.get(`mateEngineConversation:${id}`)?.content;
                const window =
                  held?.kind === "value"
                    ? (held.value as FamilyValues["mateEngineConversation"]).window
                    : undefined;
                // A split commit's parts share `from`; all but the last say `to = from`, and only the
                // first carries the header. Its sequence is the commit's, so it is held until the
                // part that moves the cursor names it.
                const header =
                  frame.to === frame.from
                    ? undefined
                    : (frame.header ??
                      (splitHeader?.from === frame.from ? splitHeader.header : undefined));
                splitHeader =
                  frame.to === frame.from && frame.header !== undefined
                    ? { from: frame.from, header: frame.header }
                    : frame.to === frame.from
                      ? splitHeader
                      : undefined;
                return Effect.map(wholeResults(key, frame.items), (items) => {
                  // A denial told while a cut result was read keeps nothing of this frame either.
                  if (closed || withheld.has(key.environmentId)) return;
                  Atom.batch(() =>
                    deliver(
                      rowsOf(
                        key,
                        frame.epoch,
                        {
                          runs: frame.runs,
                          items,
                          requests: frame.requests,
                          head: frame.to,
                          ...(header === undefined ? {} : { header }),
                        },
                        window,
                      ),
                      false,
                    ),
                  );
                  settleClosed(items);
                  cursors.set(id, { ...cursor, epoch: frame.epoch, seq: frame.to });
                });
              }
              case "synchronized": {
                if (cursor === undefined) return Effect.fail(RESUBSCRIBE);
                // A header that came alone (a restart's epoch, nothing newer) is the head's.
                if (splitHeader !== undefined) {
                  const pending = splitHeader.header;
                  splitHeader = undefined;
                  const held = store.state().facts.get(`mateEngineConversation:${id}`)?.content;
                  const window =
                    held?.kind === "value"
                      ? (held.value as FamilyValues["mateEngineConversation"]).window
                      : undefined;
                  deliver(
                    rowsOf(
                      key,
                      frame.epoch,
                      { runs: [], items: [], requests: [], head: frame.head, header: pending },
                      window,
                    ),
                    false,
                  );
                }
                cursors.set(id, { ...cursor, epoch: frame.epoch, seq: frame.head });
                Atom.batch(() => {
                  for (const scope of scopes) signal(scope, { kind: "baseline-committed" });
                  signal(link, { kind: "baseline-committed" });
                });
                return Effect.void;
              }
              case "reset":
                forget(key);
                return Effect.void;
              case "live.open":
                live.open(key, frame.itemId, frame.stream, frame.text);
                return Effect.void;
              case "live.append":
                live.append(key, frame.itemId, frame.stream, frame.offset, frame.text);
                return Effect.void;
              case "live.settle":
                live.settle(key, frame.itemId);
                return Effect.void;
              case "unserved":
                return Effect.fail(unservedFault(frame));
              case "context":
              case "progress": {
                const held = readsOfState(store.state()).fact("mateEngineGauge", id);
                const gauge: FamilyValues["mateEngineGauge"] =
                  held.kind === "known"
                    ? held.value
                    : { environmentId: key.environmentId, usage: null, progress: {} };
                const progress = { ...gauge.progress };
                if (frame.type === "progress") {
                  if (frame.value === null) delete progress[frame.itemId];
                  else progress[frame.itemId] = frame.value;
                }
                deliver(
                  [
                    {
                      family: "mateEngineGauge",
                      id,
                      value: {
                        ...gauge,
                        usage: frame.type === "context" ? frame.usage : gauge.usage,
                        progress,
                      },
                      revision: { kind: "mate-link", sequence: ++heard },
                    },
                  ],
                  false,
                );
                return Effect.void;
              }
              default:
                // Frames of a newer build: nothing this build draws.
                return Effect.void;
            }
          });
        const ended = yield* Stream.runForEach(
          wire.subscribe(key, cursors.get(id) ?? null),
          handle,
        ).pipe(
          Effect.as("ended" as const),
          Effect.catch((error) =>
            error === RESUBSCRIBE ? Effect.succeed("resubscribe" as const) : Effect.fail(error),
          ),
        );
        if (ended === "resubscribe") continue;
        return yield* Effect.fail<StreamFault>({
          outcome: "transient",
          message: "The conversation's subscription ended.",
        });
      }
    }).pipe(
      Effect.tapError((fault) =>
        Effect.sync(() => {
          if (!closed && fault.outcome === "authoritative-denial") forget(key);
        }),
      ),
    );

  const watchAccess = (environmentId: string) => {
    if (watches.has(environmentId) || wire.watch === undefined) return;
    watches.set(
      environmentId,
      Effect.runFork(wire.watch(environmentId, (fault) => receiveAccess(environmentId, fault))),
    );
  };

  /**
   * Reads the run groups before the oldest held, each run whole, into the conversation's own
   * scopes; the earlier listing's stream says while it is in flight. Nothing when the oldest held
   * is the first run, a read is in flight, or the conversation has not opened.
   */
  const readEarlier = (key: EngineConversationKey): boolean => {
    if (closed || withheld.has(key.environmentId)) return false;
    const id = engineConversationId(key);
    const cursor = cursors.get(id);
    if (cursor === undefined) return false;
    const earlier = earlierOf(readsOfState(store.state()), key);
    if (earlier === null || !earlier.more) return false;
    const oldest = earlier.before;
    const scope = engineEarlierScope(key);
    const phase = streamOf(store.state(), scope).phase;
    if (phase === "connecting" || phase === "baselining") return false;
    if (phase === "idle") signal(scope, { kind: "demand", demanded: true });
    signal(scope, phase === "refused" ? { kind: "manual-retry" } : { kind: "attempt" });
    signal(scope, { kind: "handshake" });
    const generations = Object.values(engineConversationScopes(key)).map((owned) => ({
      scope: owned,
      generation: streamOf(store.state(), owned).generation,
    }));
    const beforeOrdinal = oldest;
    const fiber = Effect.runFork(
      wire.readEarlier(key, beforeOrdinal).pipe(
        Effect.flatMap((page) =>
          page._tag === "Page"
            ? Effect.gen(function* () {
                const lacked = yield* wholeRuns(key, page.runs, page.items);
                const items = yield* wholeResults(key, [...page.items, ...lacked.items]);
                return { page, lacked: { items, requests: lacked.requests, spans: lacked.spans } };
              })
            : Effect.fail(unservedFault(page.unserved)),
        ),
        Effect.match({
          onFailure: (fault) => {
            if (!closed) signal(scope, { kind: "fault", fault, jitter: 0 });
          },
          onSuccess: ({ page, lacked }) => {
            if (closed) return;
            // Denied or unverified while the page was read: nothing of it is kept.
            const access = withheld.get(key.environmentId);
            if (access !== undefined) {
              signal(scope, { kind: "fault", fault: access, jitter: 0 });
              return;
            }
            // Where this page left off, and whether the Mate has more before it.
            const before = Math.min(beforeOrdinal, ...page.runs.map((run) => run.ordinal));
            const held = readsOfState(store.state()).fact("mateEngineGauge", id);
            const gauge: FamilyValues["mateEngineGauge"] =
              held.kind === "known"
                ? held.value
                : { environmentId: key.environmentId, usage: null, progress: {} };
            Atom.batch(() => {
              store.dispatch({
                kind: "delivery",
                via: "mate-direct",
                scopes: generations,
                reset: false,
                partial: true,
                rows: [
                  ...rowsOf(
                    key,
                    cursor.epoch,
                    {
                      runs: page.runs,
                      items: lacked.items,
                      requests: [...page.requests, ...lacked.requests],
                      head: cursor.seq,
                    },
                    undefined,
                  ),
                  ...lacked.spans.map((span) => spanRow(key, span)),
                  {
                    family: "mateEngineGauge",
                    id,
                    value: { ...gauge, earlier: { before, more: page.more } },
                    revision: { kind: "mate-link", sequence: ++heard },
                  },
                ],
                removals: [],
              });
              signal(scope, { kind: "baseline-committed" });
            });
          },
        }),
      ),
    );
    reads.add(fiber);
    fiber.addObserver(() => reads.delete(fiber));
    return true;
  };

  /**
   * Reads the next page of a run not held whole, the way its card's scroll goes: `later` after
   * the stretch it holds, `earlier` before it — from its end for a run whose start alone is held
   * (read from its start, or not at all), as a live card's scroll reaches the run before its live
   * one; it is held from its end on. Its span says while it reads; a read that fails leaves what it
   * holds, and the next ask reads again. Nothing when the run is whole that way, a page is already
   * being read, or the conversation has not opened.
   */
  const readRunPage = (
    key: EngineConversationKey,
    runId: string,
    direction: "earlier" | "later",
  ): boolean => {
    if (closed || withheld.has(key.environmentId)) return false;
    const cursor = cursors.get(engineConversationId(key));
    if (cursor === undefined) return false;
    const held = readsOfState(store.state()).fact(
      "mateEngineSpan",
      engineFactId(key.environmentId, runId),
    );
    if (held.kind !== "known") return false;
    const { environmentId: _environmentId, ...span } = held.value;
    const fromItsEnd = direction === "earlier" && span.from === null && span.to !== null;
    const at: RunPageAt | null =
      direction === "later"
        ? span.to === null
          ? null
          : { after: span.to }
        : span.from !== null
          ? { before: span.from }
          : fromItsEnd
            ? {}
            : null;
    if (span.reading !== null || at === null) return false;
    const generations = Object.values(engineConversationScopes(key)).map((owned) => ({
      scope: owned,
      generation: streamOf(store.state(), owned).generation,
    }));
    const deliver = (rows: ReadonlyArray<Row>) =>
      store.dispatch({
        kind: "delivery",
        via: "mate-direct",
        scopes: generations,
        reset: false,
        partial: true,
        rows,
        removals: [],
      });
    deliver([spanRow(key, { ...span, reading: direction })]);
    const fiber = Effect.runFork(
      wire.readRun(key, runId, at).pipe(
        Effect.flatMap((page) =>
          page._tag === "Page"
            ? Effect.map(wholeResults(key, page.items), (items) => ({ ...page, items }))
            : Effect.fail(unservedFault(page.unserved)),
        ),
        Effect.match({
          onFailure: () => {
            if (!closed) deliver([spanRow(key, span)]);
          },
          onSuccess: (page) => {
            if (closed || withheld.has(key.environmentId)) return;
            const seqs = page.items.map((item) => item.seq);
            const more = page.more && seqs.length > 0;
            const moved =
              direction === "later"
                ? { ...span, to: more ? Math.max(...seqs) : null }
                : {
                    ...span,
                    from: more ? Math.min(...seqs) : null,
                    ...(fromItsEnd ? { to: null } : {}),
                  };
            Atom.batch(() =>
              deliver([
                ...rowsOf(
                  key,
                  cursor.epoch,
                  {
                    runs: page.runs,
                    items: page.items,
                    requests: page.requests,
                    head: cursor.seq,
                  },
                  undefined,
                ),
                spanRow(key, moved),
              ]),
            );
          },
        }),
      ),
    );
    reads.add(fiber);
    fiber.addObserver(() => reads.delete(fiber));
    return true;
  };

  return {
    readEarlier,
    readRunPage,
    /** Holds the conversation while drawn; the returned release lets it go. */
    hold(key: EngineConversationKey): () => void {
      if (closed) return () => {};
      const byEnvironment = known.get(key.environmentId) ?? new Map();
      byEnvironment.set(engineConversationId(key), key);
      known.set(key.environmentId, byEnvironment);
      watchAccess(key.environmentId);
      const link = engineConversationLink(key);
      const fault = streamOf(store.state(), link).fault;
      const refusedForAccess =
        !withheld.has(key.environmentId) &&
        !links.held(link) &&
        (fault?.outcome === "access-unverified" || fault?.outcome === "authoritative-denial");
      const release = links.hold(
        link,
        Object.values(engineConversationScopes(key)),
        () => attempt(key) as Effect.Effect<never, StreamFault>,
        {
          graceMs: options.forgetAfterMs ?? ENGINE_FORGET_AFTER_MS,
          expire: () => {
            known.get(key.environmentId)?.delete(engineConversationId(key));
            forget(key);
          },
        },
      );
      // Refused for its Mate's access, held again after that access came back: it tries anew.
      if (refusedForAccess) links.held(link)?.recover();
      return release;
    },
    retry(key: EngineConversationKey) {
      links.held(engineConversationLink(key))?.retry();
    },
    /** Holds a Mate's conversation rows (its menu) while drawn. */
    holdRows(environmentId: string): () => void {
      if (closed) return () => {};
      watchAccess(environmentId);
      const link = engineRowsLink(environmentId);
      const scope = engineRowsScope(environmentId);
      return links.hold(
        link,
        [scope],
        () =>
          Effect.gen(function* () {
            const access = withheld.get(environmentId);
            if (access !== undefined) return yield* Effect.fail(access);
            signal(link, { kind: "handshake" });
            signal(scope, { kind: "attempt" });
            signal(scope, { kind: "handshake" });
            const generation = streamOf(store.state(), scope).generation;
            yield* Stream.runForEach(wire.subscribeRows(environmentId), (frame) =>
              Effect.suspend(() => {
                if (closed) return Effect.void;
                const deliver = (rows: EngineRowsFrame & { type: "snapshot" | "row" }) => {
                  const list = rows.type === "snapshot" ? rows.rows : [rows.row];
                  store.dispatch({
                    kind: "delivery",
                    via: "mate-direct",
                    scopes: [{ scope, generation }],
                    reset: rows.type === "snapshot",
                    rows: list.map((row): Row => ({
                      family: "mateEngineRow",
                      id: engineFactId(environmentId, row.conversationId),
                      value: { ...row, environmentId },
                      revision: { kind: "mate-conversation", ...row.revision },
                    })),
                    removals: [],
                  });
                };
                switch (frame.type) {
                  case "snapshot":
                  case "row":
                    deliver(frame);
                    return Effect.void;
                  case "synchronized":
                    signal(scope, { kind: "baseline-committed" });
                    signal(link, { kind: "baseline-committed" });
                    return Effect.void;
                  case "unserved":
                    return Effect.fail(unservedFault(frame));
                  default:
                    return Effect.void;
                }
              }),
            );
            return yield* Effect.fail<StreamFault>({
              outcome: "transient",
              message: "The conversation rows' subscription ended.",
            });
          }),
        null,
      );
    },
    /** Where a conversation's subscription stands: for its operations and tests. */
    cursor: (key: EngineConversationKey) => cursors.get(engineConversationId(key)) ?? null,
    close() {
      closed = true;
      links.close();
      for (const fiber of reads) void Effect.runFork(Fiber.interrupt(fiber));
      reads.clear();
      for (const fiber of watches.values()) void Effect.runFork(Fiber.interrupt(fiber));
      watches.clear();
      cursors.clear();
      known.clear();
    },
  };
}
